import {
  ArrowRight,
  ArrowUpRight,
  BadgeDollarSign,
  CalendarPlus,
  Handshake,
  Mic2,
  Rocket,
  Share2,
} from 'lucide-react';
import { Fragment, useEffect } from 'react';

import {
  type EventCredit,
  type NumberedEvent,
  type ReelMedia,
  eventHref,
  pastEvents,
  prominentUpcomingEvents,
  reelMedia,
  regularUpcomingEvents,
} from './lib/events-data.js';
import { Container, GrainOverlay, ScrollReveal } from './lib/impeccable.js';
import { Footer, Header, JOIN_URL } from './lib/site.js';
import { cn } from './lib/cn.js';

const communityBoardActions = [
  {
    title: 'Help organize',
    description:
      'Help with venue, check-in, photos, moderation, outreach, or setup.',
    cta: 'Raise your hand',
    href: 'https://docs.google.com/forms/d/e/1FAIpQLSd0xSnUSrS94T1NzerZUo_d6BbFDv5CvzDuvJtTjqnOXuuaEQ/viewform',
    icon: Handshake,
  },
  {
    title: 'Speak or host',
    description:
      'Give a talk, run a workshop, host Build Coffee, or moderate a discussion.',
    cta: 'Propose a session',
    href: 'https://docs.google.com/forms/d/e/1FAIpQLSc8MzC01m9EAVkiJpNSv2TNQUzJ54p90bHpULGTxQVvA_WS6w/viewform',
    icon: Mic2,
  },
  {
    title: 'Sponsor an event',
    description:
      'Support coffee, venue, prizes, cloud credits, food, or community logistics.',
    cta: 'Sponsor',
    href: 'https://docs.google.com/forms/d/e/1FAIpQLScrhjnjMuNX5Z92-VHqxU8TUUh-_hl9Vz3ll817gDMVfkmn9Q/viewform',
    icon: BadgeDollarSign,
  },
];

function ReelItem({ item, priority }: { item: ReelMedia; priority: boolean }) {
  return (
    <div className="reel-item rounded-lg border-border bg-secondary h-64 w-96 shrink-0 overflow-hidden border md:h-96 md:w-[40rem]">
      {item.type === 'video' ? (
        <video
          src={item.src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
      ) : (
        <picture className="block h-full w-full">
          <source srcSet={item.avifSrc} type="image/avif" />
          <img
            src={item.src}
            alt={item.alt}
            width={640}
            height={384}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'low'}
            decoding="async"
            className="h-full w-full object-cover"
          />
        </picture>
      )}
    </div>
  );
}

function PastEventsReel() {
  const half = Array.from({ length: 2 }, () => reelMedia).flat();
  const track = [...half, ...half];

  return (
    <section className="pt-24 pb-6" aria-label="Moments from past events ">
      <div className="relative overflow-hidden ">
        <div className="animate-marquee flex w-max hover:[animation-play-state:paused] ">
          {track.map((item, i) => (
            <ReelItem key={i} item={item} priority={i === 0} />
          ))}
        </div>
        <div className="from-background pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r to-transparent md:w-32" />
        <div className="from-background pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l to-transparent md:w-32" />
      </div>
    </section>
  );
}

function SectionIntro({
  title,
  description,
  className,
}: {
  title: string;
  description: string;
  className?: string;
}) {
  return (
    <div className={cn('mb-8 max-w-[58ch]', className)}>
      <h2 className="font-heading text-foreground text-[clamp(1.5rem,3vw,2.25rem)] leading-tight font-light text-balance">
        {title}
      </h2>
      <p className="text-secondary-foreground mt-3 text-sm leading-relaxed text-pretty">
        {description}
      </p>
    </div>
  );
}

function CreditItem({ item }: { item: EventCredit }) {
  if (!item.href) {
    return <span className="text-secondary-foreground">{item.name}</span>;
  }

  return (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary decoration-primary/35 hover:text-foreground underline underline-offset-4 transition-colors"
      aria-label={`${item.name} website or profile`}
    >
      {item.name}
    </a>
  );
}

function EventCredits({ event }: { event: NumberedEvent }) {
  if (!event.credits || event.credits.length === 0) return null;

  return (
    <div className="mt-3 space-y-1.5">
      {event.credits.map((group) => (
        <div
          key={group.label}
          className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-snug"
        >
          <span className="text-muted-foreground font-mono text-[0.65rem] tracking-[0.14em] uppercase">
            {group.label}
          </span>
          <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
            {group.items.map((item, index) => (
              <Fragment key={item.name}>
                <CreditItem item={item} />
                {index < group.items.length - 1 && (
                  <span className="text-muted-foreground">,</span>
                )}
              </Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

function SpeakerCredit({ event }: { event: NumberedEvent }) {
  if (!event.speaker) return null;

  const speakerName = (
    <span className="text-primary decoration-primary/35 group-hover:text-foreground underline underline-offset-4 transition-colors">
      {event.speaker}
    </span>
  );

  return (
    <span className="text-muted-foreground mt-2 flex flex-wrap items-center gap-1.5 text-sm leading-snug">
      <span className="font-mono text-[0.65rem] tracking-[0.14em] uppercase">
        by
      </span>
      {event.speakerLinkedIn ? (
        <a
          href={event.speakerLinkedIn}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-1.5 no-underline"
          aria-label={`${event.speaker} on LinkedIn`}
        >
          {speakerName}
          <ArrowUpRight
            size={13}
            className="text-primary opacity-80 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          />
        </a>
      ) : (
        speakerName
      )}
    </span>
  );
}

function LedgerRow({ event }: { event: NumberedEvent }) {
  const href = eventHref(event);
  const locationHref = event.links?.location;

  return (
    <div className="border-border not-first:border-t py-6 transition-colors md:py-7">
      <div className="flex flex-col gap-1.5 md:grid md:grid-cols-[40px_150px_minmax(0,1fr)_18rem] md:items-start md:gap-6">
        <div className="flex items-center gap-3 md:contents">
          <span className="text-primary font-mono text-xs">{event.index}</span>
          <span className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
            {[event.date ?? 'Date TBD', event.time].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div>
          {href ? (
            <a
              href={href}
              target={href.startsWith('/') ? undefined : '_blank'}
              rel={href.startsWith('/') ? undefined : 'noopener noreferrer'}
              className="font-heading text-foreground hover:text-primary text-lg font-normal transition-colors no-underline md:text-xl"
            >
              {event.title}
            </a>
          ) : (
            <span className="font-heading text-foreground text-lg font-normal md:text-xl">
              {event.title}
            </span>
          )}
          <SpeakerCredit event={event} />
          {event.details?.map((line) => (
            <span
              key={line}
              className="text-muted-foreground mt-1 block text-xs leading-relaxed"
            >
              {line}
            </span>
          ))}
          <EventCredits event={event} />
        </div>
        {locationHref ? (
          <a
            href={locationHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary flex min-w-0 items-start gap-1.5 text-sm leading-snug transition-colors no-underline md:pt-0.5"
          >
            <span className="min-w-0">{event.venue}</span>
            <ArrowUpRight
              size={13}
              className="mt-0.5 shrink-0 opacity-70 transition-opacity"
            />
          </a>
        ) : (
          <span className="text-muted-foreground flex min-w-0 items-start gap-1.5 text-sm leading-snug md:pt-0.5">
            {event.venue}
          </span>
        )}
      </div>
    </div>
  );
}

export function PastLedger({ events }: { events: NumberedEvent[] }) {
  return (
    <div>
      {events.map((event) => (
        <LedgerRow key={event.index} event={event} />
      ))}
    </div>
  );
}

function UpcomingCard({
  event,
  prominent = false,
}: {
  event: NumberedEvent;
  prominent?: boolean;
}) {
  const hasLinks = Boolean(
    event.links?.luma ??
    event.links?.calendar ??
    event.links?.social ??
    event.links?.submit,
  );

  return (
    <div
      className={cn(
        'border-border overflow-hidden border',
        prominent
          ? 'grid md:grid-cols-[minmax(22rem,30rem)_minmax(0,1fr)] md:items-start'
          : 'flex flex-col',
      )}
    >
      <div
        className={cn(
          'bg-secondary overflow-hidden',
          prominent ? 'aspect-square md:self-start' : 'aspect-square',
        )}
      >
        {event.cover ? (
          <img
            src={event.cover}
            alt={`${event.title} cover`}
            className={cn(
              'h-full w-full',
              prominent ? 'object-contain' : 'object-cover',
            )}
          />
        ) : (
          <div className="text-primary/50 flex h-full w-full items-center justify-center font-mono text-3xl">
            &gt;_
          </div>
        )}
      </div>
      <div
        className={cn('flex flex-1 flex-col gap-3 p-6', prominent && 'md:p-6')}
      >
        <span className="text-primary font-mono text-xs tracking-wider uppercase">
          {event.date ? `Next · ${event.date}` : 'Upcoming · Date TBA'}
        </span>
        <h3
          className={cn(
            'font-heading text-foreground font-light text-balance',
            prominent ? 'text-2xl' : 'text-xl',
          )}
        >
          {event.title}
        </h3>
        <p className="text-muted-foreground font-mono text-xs tracking-wider uppercase">
          {[event.format, event.time, event.venue].filter(Boolean).join(' · ')}
        </p>
        {event.details && event.details.length > 0 && (
          <div className="text-secondary-foreground max-w-[56ch] space-y-2 text-sm leading-relaxed text-pretty">
            {event.details.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        )}
        <div
          className={cn(
            'flex flex-wrap items-center gap-3 pt-2',
            prominent ? 'mt-2' : 'mt-auto',
          )}
        >
          {event.links?.luma && (
            <a
              href={event.links.luma}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary hover:bg-[#d44400] inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white transition-colors"
            >
              RSVP on Luma
              <ArrowRight size={16} />
            </a>
          )}
          {event.links?.submit && (
            <a
              href={event.links.submit}
              target="_blank"
              rel="noopener noreferrer"
              className="border-border text-foreground hover:border-primary hover:text-primary inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-medium transition-colors"
            >
              <Rocket size={16} />
              Submit a project
            </a>
          )}
          {event.links?.calendar && (
            <a
              href={event.links.calendar}
              target="_blank"
              rel="noopener noreferrer"
              className="border-border text-foreground hover:border-primary hover:text-primary inline-flex items-center gap-2 border px-4 py-2.5 text-sm font-medium transition-colors"
            >
              <CalendarPlus size={16} />
              RSVP in Google Calendar
            </a>
          )}
          {event.links?.social && (
            <a
              href={event.links.social}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="See the announcement"
              className="border-border text-muted-foreground hover:border-primary hover:text-primary inline-flex items-center justify-center border p-2.5 transition-colors"
            >
              <Share2 size={16} />
            </a>
          )}
          {!hasLinks && (
            <span className="text-muted-foreground text-sm">Details soon.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function RegularUpcomingList({
  events,
  compact = false,
}: {
  events: NumberedEvent[];
  compact?: boolean;
}) {
  if (events.length === 0) return null;

  return (
    <div className={cn('mb-8', compact && 'mb-0')}>
      <div
        className={cn(
          'border-border bg-border grid gap-px border',
          compact ? 'grid-cols-1' : 'sm:grid-cols-2 lg:grid-cols-4',
        )}
      >
        {events.map((event) => {
          const href = eventHref(event);
          const itemClass =
            'bg-background flex min-h-20 flex-col justify-between gap-2 p-3.5 transition-colors sm:p-4';
          const content = (
            <>
              <div className="flex min-w-0 items-start justify-between gap-3">
                <span className="text-foreground text-sm leading-snug font-medium text-pretty">
                  {event.title}
                </span>
                {href && (
                  <ArrowUpRight
                    size={14}
                    className="text-primary mt-0.5 shrink-0 opacity-80 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs leading-snug">
                <span className="text-primary font-mono">
                  {event.date ?? 'Date TBA'}
                </span>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground">{event.format}</span>
              </div>
            </>
          );

          if (href) {
            return (
              <a
                key={event.title}
                href={href}
                target={href.startsWith('/') ? undefined : '_blank'}
                rel={href.startsWith('/') ? undefined : 'noopener noreferrer'}
                className={cn(
                  itemClass,
                  'group hover:bg-secondary no-underline',
                )}
              >
                {content}
              </a>
            );
          }

          return (
            <div key={event.title} className={itemClass}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UpcomingBlock() {
  const hasUpcoming =
    prominentUpcomingEvents.length > 0 || regularUpcomingEvents.length > 0;
  const prominentEvent = prominentUpcomingEvents[0];
  const listEvents = [
    ...regularUpcomingEvents,
    ...prominentUpcomingEvents.slice(1),
  ];

  if (!hasUpcoming) return <UpcomingEmpty />;

  return (
    <div>
      {listEvents.length > 0 && <RegularUpcomingList events={listEvents} />}
      {prominentEvent && <UpcomingCard event={prominentEvent} prominent />}
    </div>
  );
}

function UpcomingEmpty() {
  return (
    <div className="border-border flex flex-col items-center border border-dashed px-8 py-16 text-center">
      <span className="border-border text-primary mb-6 inline-flex size-12 items-center justify-center border font-mono text-lg">
        &gt;_
      </span>
      <p className="font-heading text-foreground text-xl font-light">
        Next one's being planned.
      </p>
      <p className="text-muted-foreground mx-auto mt-3 max-w-[42ch] text-sm leading-relaxed">
        We meet biweekly in Amman. Join the community and you'll get the Luma
        invite before it goes out anywhere else.
      </p>
      <a
        href={JOIN_URL}
        className="bg-primary hover:bg-[#d44400] mt-8 inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white transition-colors"
      >
        Join the Community
        <ArrowRight size={16} />
      </a>
    </div>
  );
}

function CommunityBoard() {
  return (
    <div>
      <div className="grid gap-4 md:grid-cols-3">
        {communityBoardActions.map((action) => {
          const Icon = action.icon;

          return (
            <a
              key={action.title}
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className="border-border hover:border-primary group flex min-h-56 flex-col border p-5 no-underline transition-colors"
            >
              <span className="border-border text-primary mb-5 inline-flex size-10 items-center justify-center border">
                <Icon size={18} />
              </span>
              <h3 className="font-heading text-foreground text-xl font-light text-balance">
                {action.title}
              </h3>
              <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed text-pretty">
                {action.description}
              </p>
              <span className="text-primary mt-6 inline-flex items-center gap-2 text-sm font-semibold">
                {action.cta}
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function EventsIntro() {
  return (
    <section className="pt-10 pb-8">
      <Container>
        <span className="text-muted-foreground mb-4 block font-mono text-[0.625rem] font-medium tracking-[0.15em] uppercase">
          Events · Field Log
        </span>
        <h1 className="font-heading text-foreground text-[clamp(2.25rem,5vw,4rem)] leading-[1.1] font-light tracking-[-0.02em] text-balance">
          In person, in Amman.
        </h1>
        <p className="text-secondary-foreground mt-6 max-w-[58ch] text-lg leading-relaxed text-pretty">
          Biweekly meetups for AI engineers. Free to attend, no intro-to-AI
          content — talks, hackathons, workshops, Build Coffee, and networking
          events with people who ship. Here's everything we've done, and what's
          next.
        </p>
      </Container>
    </section>
  );
}

function JoinStrip() {
  return (
    <section className="border-border border-t py-16">
      <Container>
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-heading text-foreground text-[clamp(1.25rem,2.5vw,1.75rem)] font-light">
            Never miss one.
          </p>
          <a
            href={JOIN_URL}
            className="bg-primary hover:bg-[#d44400] inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
          >
            Join the Community
            <ArrowRight size={16} />
          </a>
        </div>
      </Container>
    </section>
  );
}

export default function Events() {
  useEffect(() => {
    document.title = 'The Agent Lab — Events';
  }, []);

  return (
    <div className="min-h-dvh">
      <GrainOverlay />
      <Header />
      <PastEventsReel />
      <EventsIntro />

      <section className="py-12">
        <Container>
          <ScrollReveal>
            <SectionIntro
              title="Upcoming events"
              description="The next confirmed date is featured below. Planned sessions stay in the queue until dates are locked."
            />
            <UpcomingBlock />
          </ScrollReveal>
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <ScrollReveal>
            <SectionIntro
              title="Help shape what happens next."
              description="Raise your hand if you want to organize, speak, host, or sponsor a future Agent Lab event."
            />
            <CommunityBoard />
          </ScrollReveal>
        </Container>
      </section>

      <section className="py-12">
        <Container>
          <ScrollReveal>
            <SectionIntro
              title="Past events"
              description="A running record of talks, hackathons, workshops, Build Coffee, and networking events the community has already hosted."
            />
            <PastLedger events={pastEvents} />
          </ScrollReveal>
        </Container>
      </section>

      <JoinStrip />
      <Footer />
    </div>
  );
}
